import { useState } from 'react'
import { useAuthStore } from '../../stores/auth'
import { CifreAzi } from './CifreAzi'
import { BandaSolicitari, DeRezolvat } from './DeRezolvat'
import { BaraJos, Noutati, Scurtaturi, TablouDepartament, scurtaturiPentru } from './Lateral'
import { Sectiune } from './Sectiune'
import { useSurseDate, type SursaDate } from './date'

/**
 * Acasa — pagina de start a fiecaruia, pe rol (Madalin, 13.10.2026: „fara o gramada de date
 * aruncate pe ecran in care te pierzi in primele 5 secunde").
 *
 * Raspunde la „ce am de facut acum?". Ce vede omul hotaraste serverul, sursa cu sursa, dupa
 * drepturile modulelor lui — aici nu se ascunde nimic, doar se arata ce a venit. Plan:
 * `docs/superpowers/plans/2026-10-14-dashboard-acasa-plan.md`.
 */
const NUME_ROL: Record<string, string> = {
  admin: 'Admin · toate departamentele',
  contabilitate: 'Contabilitate',
  vanzari: 'Vânzări · portofoliul tău',
  manager_vanzari: 'Manager vânzări · echipa ta',
  marketing: 'Marketing',
  depozit: 'Depozit',
  administrativ: 'Operațional',
}

function salut(d = new Date()): string {
  const h = d.getHours()
  return h < 11 ? 'Bună dimineața' : h < 18 ? 'Bună ziua' : 'Bună seara'
}

export function AcasaPage() {
  const user = useAuthStore((s) => s.user)
  const permissions = useAuthStore((s) => s.permissions)
  const [alegCifre, setAlegCifre] = useState(false)
  const prenume = user?.name.split(/\s+/)[0] ?? ''
  const scurtaturi = scurtaturiPentru(user?.role, permissions)
  const areTaskuri = permissions.includes('tasks:read')
  const azi = new Date().toLocaleDateString('ro-RO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  return (
    <div className="grid gap-4">
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <h1 className="text-titlu text-b4l-text">
            {salut()}
            {prenume && `, ${prenume}`}
          </h1>
          <p className="text-corp text-b4l-text-muted">
            {user?.jobTitle || NUME_ROL[user?.role ?? ''] || ''} · {azi}
          </p>
        </div>
        <Prospetime />
      </header>

      <BandaSolicitari />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="grid content-start gap-4">
          <Sectiune id="rezolvat" utilizator={user?.id} titlu="De rezolvat">
            <DeRezolvat utilizator={user?.id} />
          </Sectiune>

          <Sectiune
            id="cifre"
            utilizator={user?.id}
            titlu="Azi, în cifre"
            caseta={false}
            actiune={
              <button
                type="button"
                onClick={() => setAlegCifre((v) => !v)}
                className="text-mic font-semibold text-b4l-blue hover:underline"
              >
                {alegCifre ? 'Renunță' : 'Alege cifrele'}
              </button>
            }
          >
            <CifreAzi utilizator={user?.id} alege={alegCifre} laAles={() => setAlegCifre(false)} />
          </Sectiune>

          {areTaskuri && (
            <Sectiune
              id="departament"
              utilizator={user?.id}
              titlu={user?.role === 'admin' ? 'Departamentele' : 'Departamentul tău'}
              sub="task-uri deschise"
            >
              <TablouDepartament admin={user?.role === 'admin'} />
            </Sectiune>
          )}
        </div>

        <aside className="grid content-start gap-4" aria-label="Lateral">
          {scurtaturi.length > 0 && (
            <div className="hidden sm:block">
              <Sectiune id="scurtaturi" utilizator={user?.id} titlu="Scurtături">
                <Scurtaturi lista={scurtaturi} />
              </Sectiune>
            </div>
          )}
          <Sectiune id="noutati" utilizator={user?.id} titlu="Noutăți" sub="fără acțiune">
            <Noutati />
          </Sectiune>
        </aside>
      </div>

      <BaraJos lista={scurtaturi} />
    </div>
  )
}

const deCand = (iso: string) => {
  const d = new Date(iso)
  const azi = new Date()
  const ora = d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
  return d.toDateString() === azi.toDateString()
    ? ora
    : `${d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })}, ${ora}`
}

/** Fiecare sursa cu ora ultimei citiri; galben daca e veche — „0" dupa un sync picat minte. */
function Prospetime() {
  const q = useSurseDate()
  const surse = q.data?.surse ?? []
  if (surse.length === 0) return null
  const veche = (s: SursaDate) =>
    !s.la || Date.now() - new Date(s.la).getTime() > s.pragOre * 3_600_000
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Cât de proaspete sunt datele">
      {surse.map((s) => (
        <li
          key={s.id}
          className={`rounded-full px-2 py-0.5 text-mic ${
            veche(s) ? 'bg-b4l-amber-light text-b4l-amber-dark' : 'bg-b4l-bg text-b4l-text-muted'
          }`}
          title={veche(s) ? 'Datele sunt mai vechi decât de obicei' : undefined}
        >
          {s.nume} {s.la ? deCand(s.la) : '— n-a rulat'}
        </li>
      ))}
    </ul>
  )
}
